import { takeLatest, call } from "redux-saga/effects";
import axiosInstance, {
  scriptsRequest,
  ratingRequest,
  favoriteRequest,
  followRequest,
} from "services/httpService";
import { successHandler } from "helpers/httpHelpers";
import * as types from "./types";
import safeSaga from "helpers/safeSaga";
import { convertParamsToString } from "helpers/stringManipulation";
import { successToast } from "utils/hooks";

function* getScripts({ payload }) {
  const response = yield call(
    [axiosInstance, "get"],
    scriptsRequest.SCRIPTS + "?page=" + String(payload),
  );

  yield successHandler(response, types.GET_SCRIPTS.success);
}
function* getScriptsAdmin({ payload }) {
  const response = yield call([axiosInstance, "get"], scriptsRequest.SCRIPTS_ADMIN);

  yield successHandler(response, types.GET_SCRIPTS_ADMIN.success);
}
function* getScript({ payload }: Record<string, any>) {
  const url = convertParamsToString(scriptsRequest.SCRIPT, { id: payload });
  const response = yield call([axiosInstance, "get"], url);

  response.data.data = {
    ...response.data.data,
    categoryNeedsReview: response.data.categoryNeedsReview,
    languageNeedsReview: response.data.languageNeedsReview,
  };
  yield successHandler(response, types.GET_SCRIPT.success);
}
function* verifyScript({ payload }: Record<string, any>) {
  const url = convertParamsToString(scriptsRequest.SCRIPTS_VERIFY, { id: payload.id });
  const response = yield call([axiosInstance, "put"], url, {
    status: payload.status,
    composerId: payload.composerId,
    category: payload.category,
    ...(payload.categoryIds ? { categoryIds: payload.categoryIds } : {}),
    ...(payload.languageIds ? { languageIds: payload.languageIds } : {}),
  });

  yield successHandler(response, types.VERIFY_SCRIPT.success);
}

function* userInfo({ payload }: Record<string, any>) {
  const url = convertParamsToString(scriptsRequest.CURRENT_USER_INFO_FOR_SCRIPT, {
    scriptId: payload,
  });
  const response = yield call([axiosInstance, "get"], url);

  yield successHandler(response, types.CURRENT_USER_INFO_SCRIPT.success);
}
function* deleteScript({ payload }: Record<string, any>) {
  const url = convertParamsToString(scriptsRequest.SCRIPT, { id: payload });

  yield call([axiosInstance, "delete"], url);
  const response = {
    status: 200,
    data: {
      id: payload,
    },
  };
  yield successHandler(response, types.DELETE_SCRIPT.success, "Script Deleted Successfully");
  // yield put({ payload: response.data, type: types.UPDATE_MY_SCRIPTS.success });
}
function* myScripts() {
  const response = yield call([axiosInstance, "get"], scriptsRequest.MY_SCRIPTS);

  yield successHandler(response, types.MY_SCRIPTS.success);
}
function* rateScript({ payload }) {
  const url = convertParamsToString(ratingRequest.RATE_SCRIPT, { id: payload.id });
  const response = yield call([axiosInstance, "put"], url, { rating: payload.rating });

  yield successHandler(response, types.RATE_SCRIPT.success);
}

function* addFavorite({ payload }) {
  const url = convertParamsToString(favoriteRequest.FAVOURITE_ID, { id: payload.id });
  const response = yield call([axiosInstance, "post"], url);

  yield successHandler(response, types.ADD_FAVOURITE.success);
}

function* removeFavourite({ payload }) {
  const url = convertParamsToString(favoriteRequest.FAVOURITE_ID, { id: payload.id });
  const response = yield call([axiosInstance, "delete"], url);

  yield successHandler(response, types.REMOVE_FAVOURITE.success);
}
function* followUser({ payload }) {
  const url = convertParamsToString(followRequest.FOLLOW_ID, { id: payload });
  const response = yield call([axiosInstance, "put"], url);

  yield successHandler(response, types.FOLLOW_USER.success);
}

function* unfollowUser({ payload }) {
  const url = convertParamsToString(followRequest.FOLLOW_ID, { id: payload });
  const response = yield call([axiosInstance, "delete"], url);

  yield successHandler(response, types.UNFOLLOW_USER.success);
}
function* searchScript({ payload }) {
  const response = yield call([axiosInstance, "get"], scriptsRequest.SEARCH + String(payload));

  yield successHandler(response, types.SEARCH_SCRIPT.success);
}
function* searchResult({ payload }) {
  const response = yield call([axiosInstance, "get"], scriptsRequest.SCRIPTS + String(payload));

  yield successHandler(response, types.SEARCH_RESULT.success);
}

function* editScriptAdmin({ payload }) {
  if (payload instanceof FormData) {
    const url = convertParamsToString(scriptsRequest.EDIT_SCRIPT, {
      id: String(payload.get("id") ?? ""),
    });
    yield call([axiosInstance, "patch"], url, payload);
  } else {
    const { id, ...requestPayload } = payload;
    const url = convertParamsToString(scriptsRequest.EDIT_SCRIPT, { id });
    yield call([axiosInstance, "patch"], url, requestPayload);
  }
  successToast("Successfully Updated Script");
}
function* updateScriptMedia({ payload }: Record<string, any>) {
  const url = convertParamsToString(scriptsRequest.UPDATE_SCRIPT_MEDIA, { slug: payload.slug });
  yield call([axiosInstance, "patch"], url, payload.formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}
export default function* authSaga(): unknown {
  yield takeLatest(types.GET_SCRIPTS.request, safeSaga(getScripts, types.GET_SCRIPTS.error));
  yield takeLatest(types.GET_SCRIPT.request, safeSaga(getScript, types.GET_SCRIPT.error));
  yield takeLatest(types.MY_SCRIPTS.request, safeSaga(myScripts, types.MY_SCRIPTS.error));
  yield takeLatest(types.RATE_SCRIPT.request, safeSaga(rateScript, types.RATE_SCRIPT.error));
  yield takeLatest(types.ADD_FAVOURITE.request, safeSaga(addFavorite, types.ADD_FAVOURITE.error));
  yield takeLatest(
    types.REMOVE_FAVOURITE.request,
    safeSaga(removeFavourite, types.REMOVE_FAVOURITE.error),
  );
  yield takeLatest(
    types.CURRENT_USER_INFO_SCRIPT.request,
    safeSaga(userInfo, types.CURRENT_USER_INFO_SCRIPT.error),
  );
  yield takeLatest(types.FOLLOW_USER.request, safeSaga(followUser, types.FOLLOW_USER.error));
  yield takeLatest(types.UNFOLLOW_USER.request, safeSaga(unfollowUser, types.UNFOLLOW_USER.error));
  yield takeLatest(types.SEARCH_SCRIPT.request, safeSaga(searchScript, types.SEARCH_SCRIPT.error));
  yield takeLatest(types.SEARCH_RESULT.request, safeSaga(searchResult, types.SEARCH_RESULT.error));
  yield takeLatest(types.DELETE_SCRIPT.request, safeSaga(deleteScript, types.DELETE_SCRIPT.error));
  yield takeLatest(
    types.GET_SCRIPTS_ADMIN.request,
    safeSaga(getScriptsAdmin, types.GET_SCRIPTS_ADMIN.error),
  );
  yield takeLatest(types.VERIFY_SCRIPT.request, safeSaga(verifyScript, types.VERIFY_SCRIPT.error));
  yield takeLatest(types.EDIT_SCRIPT.request, safeSaga(editScriptAdmin, types.EDIT_SCRIPT.error));
  yield takeLatest(
    types.UPDATE_SCRIPT_MEDIA.request,
    safeSaga(updateScriptMedia, types.UPDATE_SCRIPT_MEDIA.error),
  );
}
